import { gql, useQuery } from '@apollo/client';
import { Loading } from '@everlutionsk/ui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, alpha, Box, ButtonBase, Collapse, Paper, Stack, Typography } from '@mui/material';
import React, { ReactNode, useState } from 'react';
import { SummaryCard, SummaryCardBreakdownRow } from '../components/assuranceSummaryCard';
import { AdminDoughnutChart, DoughnutSlice } from '../components/charts/doughnutChart';
import { Badge } from '../components/coreComponents/badge';
import { Button } from '../components/coreComponents/button';
import { Table } from '../components/microLayouts/table';

// Admin theme maps `text.primary` to "text-on-accent" (white). Anything
// using the default MUI Typography text colour disappears against the
// neutral background. The variables below resolve to explicit, visible
// palette tokens — use them everywhere on this screen.
const ON_NEUTRAL = (theme: { brandPalette: { foreground: { neutral: string } } }) =>
  theme.brandPalette.foreground.neutral;
const ON_NEUTRAL_FADED = (theme: { brandPalette: { foreground: { neutralFaded: string } } }) =>
  theme.brandPalette.foreground.neutralFaded;

interface Statistics {
  readonly accountId: string;
  readonly alias?: string | null;
  readonly createdAt: string;
  readonly publishedAt?: string | null;

  readonly userCountTotal: number;
  readonly userCountActive: number;
  readonly userCountInvited: number;
  readonly userCountArchived: number;
  readonly lastUserLoginAt?: string | null;

  readonly organisationCount: number;
  readonly rootOrganisationId?: string | null;
  readonly rootOrganisationName?: string | null;
  readonly rootOrganisationHasApprovedContext: boolean;
  readonly rootOrganisationHasBillingAddress: boolean;
  readonly rootOrganisationHasIdentificationNumber: boolean;

  readonly frameworkCount: number;
  readonly approvedFrameworkConfigCount: number;

  readonly stripeCustomerConnected: boolean;

  readonly businessServiceCount: number;
  readonly businessFunctionCount: number;
  readonly enablingServiceCount: number;
  readonly policyCount: number;
  readonly processCount: number;
  readonly goalCount: number;
  readonly securityProjectCount: number;
  readonly otherAssetCount: number;
  readonly customerCount: number;
  readonly supplierCount: number;
  readonly interestedPartyCount: number;

  readonly riskCount: number;
  readonly riskAssessmentFinalisedCount: number;
  readonly riskAssessmentDraftCount: number;

  readonly kpiCount: number;
  readonly eventCount: number;

  readonly campaignCount: number;
  readonly campaignPublishedCount: number;
  readonly videoCount: number;
  readonly videoPublishedCount: number;
  readonly videoWatchEventCount: number;
  readonly assessmentCount: number;
  readonly assessmentSubmissionCompletedCount: number;
  readonly trainingAccessCount: number;

  readonly taskCount: number;
  readonly taskOpenCount: number;
  readonly taskCompletedCount: number;
  readonly taskOverdueCount: number;

  readonly auditPlanCount: number;
  readonly auditPlanCompletedCount: number;
  readonly auditPlanInProgressCount: number;
  readonly auditFindingCount: number;
  readonly auditFindingOpenCount: number;

  readonly measureCount: number;
  readonly measureMonitoredCount: number;
  readonly biaCount: number;
  readonly vulnerabilityCount: number;
  readonly organisationVulnerabilityCount: number;

  readonly accessValidationCheckCount: number;
  readonly accessValidationFindingOpenCount: number;
  readonly complianceRuleCount: number;

  readonly ssoConfigured: boolean;
  readonly connectorCount: number;
  readonly legalRegisterEntryCount: number;
}

interface KpiTile {
  readonly label: string;
  readonly value: ReactNode;
  readonly color: string;
  readonly breakdown: readonly SummaryCardBreakdownRow[];
}

interface KpiTileGroup {
  readonly key: string;
  readonly heading: string;
  readonly tiles: readonly KpiTile[];
  // Column count per breakpoint, chosen so the tile count divides evenly
  // — no half-empty rows when the grid wraps.
  readonly columns: { xs: number; sm: number; md: number; lg: number; xl: number };
}

interface ChecklistRow {
  readonly label: string;
  readonly checked?: boolean;
  readonly count?: number;
  readonly hint?: string;
}

interface ChecklistSection {
  readonly title: string;
  readonly description?: string;
  readonly rows: readonly ChecklistRow[];
  // Optional accent — used for the left border stripe + header icon so the
  // section visually ties back to a tile of the same colour above.
  readonly accentColor?: string;
}

export function AccountStatisticsTab({ accountId }: { accountId: string }) {
  const { data, loading, error, refetch } = useQuery(accountStatisticsQuery, {
    variables: { id: accountId },
    fetchPolicy: 'cache-and-network'
  });

  if (loading && data == null) return <Loading />;
  if (error != null) {
    return (
      <Alert
        severity="error"
        action={
          <Button variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Failed to load statistics: {error.message}
      </Alert>
    );
  }

  const stats: Statistics | null = data?.accountStatistics ?? null;
  if (stats == null) {
    return <Typography sx={{ color: ON_NEUTRAL }}>Statistics not available.</Typography>;
  }

  const tileGroups = buildKpiTileGroups(stats);
  const sections = buildSections(stats);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: ON_NEUTRAL_FADED }}>
          Live snapshot from the tenant database. Counts and flags reflect the state at the moment
          this page was loaded.
        </Typography>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          startIcon={<RefreshIcon fontSize="small" />}
        >
          Refresh
        </Button>
      </Stack>

      <Stack spacing={4} sx={{ mb: 6 }}>
        {tileGroups.map(group => (
          <KpiTileGroupRow key={group.key} group={group} />
        ))}
      </Stack>

      <Box sx={{ mb: 6 }}>
        <Typography
          variant="labelXSmall"
          sx={{
            color: ON_NEUTRAL_FADED,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600,
            display: 'block',
            mb: 1.5
          }}
        >
          Composition
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
          }}
        >
          <AdminDoughnutChart
            title="Business context"
            description="Asset records grouped by typeId. Hover any slice for exact counts."
            slices={buildBusinessContextSlices(stats)}
            totalLabel="Records"
          />
          <AdminDoughnutChart
            title="External parties"
            description="People-type assets, split by their relationship to the tenant."
            slices={buildPartiesSlices(stats)}
            totalLabel="Parties"
          />
        </Box>
      </Box>

      <Stack spacing={6}>
        {sections.map(section => (
          <ChecklistCard key={section.title} section={section} />
        ))}
      </Stack>
    </Box>
  );
}

function KpiTileGroupRow({ group }: { group: KpiTileGroup }) {
  const { columns } = group;
  const [open, setOpen] = useState(true);
  const headerId = `stats-tile-group-${group.key}`;
  const panelId = `${headerId}-panel`;

  return (
    <Box>
      <ButtonBase
        component="div"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-controls={panelId}
        id={headerId}
        focusRipple
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          width: '100%',
          justifyContent: 'flex-start',
          py: 0.5,
          px: 0.5,
          borderRadius: 0.5,
          textAlign: 'left',
          transition: 'background-color 120ms ease',
          '&:hover': {
            backgroundColor: theme => alpha(theme.brandPalette.foreground.neutral, 0.04)
          }
        }}
      >
        <Typography
          variant="labelXSmall"
          sx={{
            color: ON_NEUTRAL_FADED,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600
          }}
        >
          {group.heading}
        </Typography>
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.125,
            borderRadius: 999,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: 0.3,
            color: ON_NEUTRAL_FADED,
            backgroundColor: theme => alpha(theme.brandPalette.foreground.neutralFaded, 0.12)
          }}
        >
          {group.tiles.length}
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            ml: 'auto',
            color: ON_NEUTRAL_FADED,
            transition: 'transform 180ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </ButtonBase>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          role="region"
          id={panelId}
          aria-labelledby={headerId}
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: {
              xs: `repeat(${columns.xs}, 1fr)`,
              sm: `repeat(${columns.sm}, 1fr)`,
              md: `repeat(${columns.md}, 1fr)`,
              lg: `repeat(${columns.lg}, 1fr)`,
              xl: `repeat(${columns.xl}, 1fr)`
            }
          }}
        >
          {group.tiles.map(tile => (
            <SummaryCard
              key={tile.label}
              title={tile.label}
              value={tile.value}
              color={tile.color}
              breakdown={tile.breakdown}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function ChecklistCard({ section }: { section: ChecklistSection }) {
  const accent = section.accentColor ?? '#9AA5B1';
  const [open, setOpen] = useState(true);
  const headerId = `stats-section-${section.title.replace(/\s+/g, '-').toLowerCase()}`;
  const panelId = `${headerId}-panel`;

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        backgroundColor: theme => theme.brandPalette.background.neutral,
        borderLeft: '3px solid',
        borderLeftColor: accent
      }}
    >
      <ButtonBase
        component="div"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-controls={panelId}
        id={headerId}
        focusRipple
        sx={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 4,
          py: 3,
          borderBottom: open ? 1 : 0,
          borderColor: 'divider',
          backgroundColor: 'transparent',
          transition: 'background-color 120ms ease',
          '&:hover': {
            backgroundColor: theme => alpha(theme.brandPalette.foreground.neutral, 0.03)
          }
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={2}>
            <Typography variant="labelLarge" sx={{ fontWeight: 600, color: ON_NEUTRAL }}>
              {section.title}
            </Typography>
            <Typography
              variant="labelSmall"
              sx={{ color: ON_NEUTRAL_FADED, ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {`${section.rows.length} ${section.rows.length === 1 ? 'item' : 'items'}`}
            </Typography>
          </Stack>
          {section.description != null && (
            <Typography
              variant="labelSmall"
              sx={{ color: ON_NEUTRAL_FADED, display: 'block', lineHeight: 1.55, mt: 0.75 }}
            >
              {section.description}
            </Typography>
          )}
        </Box>
        <ExpandMoreIcon
          sx={{
            color: ON_NEUTRAL_FADED,
            flexShrink: 0,
            transition: 'transform 180ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        />
      </ButtonBase>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box role="region" id={panelId} aria-labelledby={headerId} sx={{ p: 2 }}>
          <Table
            items={section.rows}
            rowKey={row => row.label}
            columns={[
              {
                label: 'Status',
                render: row => <CheckIndicator checked={row.checked} accent={accent} />
              },
              {
                label: 'Metric',
                render: row => (
                  <Box>
                    <Typography variant="labelSmall" sx={{ color: ON_NEUTRAL, display: 'block' }}>
                      {row.label}
                    </Typography>
                    {row.hint != null && (
                      <Typography
                        variant="labelXSmall"
                        sx={{ color: ON_NEUTRAL_FADED, display: 'block', mt: 0.5 }}
                      >
                        {row.hint}
                      </Typography>
                    )}
                  </Box>
                )
              },
              {
                label: 'Value',
                align: 'right',
                render: row => <ValueDisplay row={row} />
              }
            ]}
          />
        </Box>
      </Collapse>
    </Paper>
  );
}

function CheckIndicator({ checked, accent }: { checked?: boolean; accent: string }) {
  if (checked === true) {
    return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22, display: 'block' }} />;
  }
  if (checked === false) {
    return (
      <RadioButtonUncheckedIcon sx={{ color: ON_NEUTRAL_FADED, fontSize: 22, display: 'block' }} />
    );
  }
  // Number-only row — render a small accent-coloured dot so the column
  // still visually scans as a column.
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: accent,
        opacity: 0.5,
        ml: 0.875
      }}
    />
  );
}

function ValueDisplay({ row }: { row: ChecklistRow }) {
  if (row.count != null) {
    return (
      <Typography variant="labelSmall" sx={{ fontWeight: 700, color: ON_NEUTRAL }}>
        {row.count.toLocaleString()}
      </Typography>
    );
  }
  if (row.checked === true) {
    return <Badge variant="positive" notification="Yes" isUppercase />;
  }
  if (row.checked === false) {
    return <Badge variant="default" notification="No" isUppercase />;
  }
  return (
    <Typography variant="labelSmall" sx={{ color: ON_NEUTRAL_FADED }}>
      —
    </Typography>
  );
}

function buildKpiTileGroups(stats: Statistics): readonly KpiTileGroup[] {
  // Two tile groups; the Business context and External parties data is
  // rendered as doughnut charts below the Operations row instead of as
  // tiles — proportions read better visually than a row of stat tiles
  // when there are many slices of varying size.
  return [
    {
      key: 'headline',
      heading: 'Headline metrics',
      tiles: buildHeadlineTiles(stats),
      columns: { xs: 2, sm: 3, md: 3, lg: 6, xl: 6 }
    },
    {
      key: 'operations',
      heading: 'Operations & education',
      tiles: buildOperationsTiles(stats),
      columns: { xs: 2, sm: 3, md: 3, lg: 6, xl: 6 }
    }
  ];
}

function buildHeadlineTiles(stats: Statistics): readonly KpiTile[] {
  const lastLogin = stats.lastUserLoginAt != null ? formatRelative(stats.lastUserLoginAt) : 'Never';
  return [
    {
      label: 'Users',
      value: stats.userCountTotal.toLocaleString(),
      color: '#0078D4',
      breakdown: [
        { label: 'Active', value: stats.userCountActive.toLocaleString() },
        { label: 'Invited', value: stats.userCountInvited.toLocaleString() }
      ]
    },
    {
      label: 'Organisations',
      value: stats.organisationCount.toLocaleString(),
      color: '#5C2D91',
      breakdown: [
        { label: 'Approved context', value: yesNo(stats.rootOrganisationHasApprovedContext) },
        { label: 'Billing address', value: yesNo(stats.rootOrganisationHasBillingAddress) }
      ]
    },
    {
      label: 'Risks',
      value: stats.riskCount.toLocaleString(),
      color: '#D83B01',
      breakdown: [
        { label: 'Finalised', value: stats.riskAssessmentFinalisedCount.toLocaleString() },
        { label: 'Drafts', value: stats.riskAssessmentDraftCount.toLocaleString() }
      ]
    },
    {
      label: 'Frameworks',
      value: stats.frameworkCount.toLocaleString(),
      color: '#107C10',
      breakdown: [
        { label: 'Approved configs', value: stats.approvedFrameworkConfigCount.toLocaleString() },
        { label: 'Stripe connected', value: yesNo(stats.stripeCustomerConnected) }
      ]
    },
    {
      label: 'Activity',
      value: lastLogin,
      color: '#A4262C',
      breakdown: [
        { label: 'Events logged', value: stats.eventCount.toLocaleString() },
        { label: 'KPIs configured', value: stats.kpiCount.toLocaleString() }
      ]
    },
    {
      label: 'Account state',
      value: stats.publishedAt != null ? 'Published' : 'Draft',
      color: '#E3008C',
      breakdown: [
        { label: 'Published', value: yesNo(stats.publishedAt != null) },
        { label: 'Has IČO', value: yesNo(stats.rootOrganisationHasIdentificationNumber) }
      ]
    }
  ];
}

// Lively donut palette — saturated Material-A400/A700 hues spread evenly
// around the colour wheel so adjacent slices contrast even when small.
function buildBusinessContextSlices(stats: Statistics): readonly DoughnutSlice[] {
  return [
    { label: 'Business services', value: stats.businessServiceCount, color: '#1DE9B6' }, // teal A400
    { label: 'Business functions', value: stats.businessFunctionCount, color: '#00B0FF' }, // light-blue A400
    { label: 'Enabling services', value: stats.enablingServiceCount, color: '#7C4DFF' }, // deep-purple A200
    { label: 'Other assets', value: stats.otherAssetCount, color: '#FF4081' }, // pink A200
    { label: 'Policies', value: stats.policyCount, color: '#FF6D00' }, // orange A700
    { label: 'Processes', value: stats.processCount, color: '#FFD600' }, // yellow A700
    { label: 'Goals', value: stats.goalCount, color: '#00E676' }, // green A400
    { label: 'Security projects', value: stats.securityProjectCount, color: '#F50057' } // pink A400
  ];
}

function buildPartiesSlices(stats: Statistics): readonly DoughnutSlice[] {
  return [
    { label: 'Customers', value: stats.customerCount, color: '#00E5FF' }, // cyan A400
    { label: 'Suppliers', value: stats.supplierCount, color: '#FFAB00' }, // amber A700
    { label: 'Interested parties', value: stats.interestedPartyCount, color: '#D500F9' } // purple A400
  ];
}

function buildOperationsTiles(stats: Statistics): readonly KpiTile[] {
  return [
    {
      label: 'Education',
      value: (stats.campaignCount + stats.videoCount).toLocaleString(),
      color: '#C239B3', // magenta
      // Subsets sum to the primary: videos + campaigns === primary.
      breakdown: [
        { label: 'Videos', value: stats.videoCount.toLocaleString() },
        { label: 'Campaigns', value: stats.campaignCount.toLocaleString() }
      ]
    },
    {
      label: 'Tasks',
      value: stats.taskCount.toLocaleString(),
      color: '#515C6B', // slate
      // Subsets sum to the primary: open + done === taskCount. "Done"
      // includes both completed and closed statuses (anything not open).
      breakdown: [
        { label: 'Open', value: stats.taskOpenCount.toLocaleString() },
        {
          label: 'Done',
          value: Math.max(stats.taskCount - stats.taskOpenCount, 0).toLocaleString()
        }
      ]
    },
    {
      label: 'Audits',
      value: stats.auditPlanCount.toLocaleString(),
      color: '#8E562E', // brown
      breakdown: [
        { label: 'In progress', value: stats.auditPlanInProgressCount.toLocaleString() },
        { label: 'Open findings', value: stats.auditFindingOpenCount.toLocaleString() }
      ]
    },
    {
      label: 'Measures',
      value: stats.measureCount.toLocaleString(),
      color: '#498205', // olive
      // Breakdown items are subsets of the primary so the numbers add up:
      // monitored + (everything else) === measureCount.
      breakdown: [
        { label: 'Monitored', value: stats.measureMonitoredCount.toLocaleString() },
        {
          label: 'In treatment',
          value: Math.max(stats.measureCount - stats.measureMonitoredCount, 0).toLocaleString()
        }
      ]
    },
    {
      label: 'BIAs',
      value: stats.biaCount.toLocaleString(),
      color: '#038387', // deep teal
      // `biaCount` is now distinct BIAs with at least one BiaVersion — i.e.
      // started BIAs. The breakdown highlights asset coverage so the number
      // has context.
      breakdown: [
        {
          label: 'Total assets',
          value: (
            stats.businessServiceCount +
            stats.businessFunctionCount +
            stats.enablingServiceCount +
            stats.otherAssetCount
          ).toLocaleString()
        },
        { label: 'Risks covered', value: stats.riskCount.toLocaleString() }
      ]
    },
    {
      label: 'Vulnerabilities',
      value: stats.vulnerabilityCount.toLocaleString(),
      color: '#B85D4D', // terracotta
      breakdown: [
        {
          label: 'Applicable to org',
          value: stats.organisationVulnerabilityCount.toLocaleString()
        },
        {
          label: 'Catalogue-only',
          value: Math.max(
            stats.vulnerabilityCount - stats.organisationVulnerabilityCount,
            0
          ).toLocaleString()
        }
      ]
    }
  ];
}

function buildSections(stats: Statistics): readonly ChecklistSection[] {
  return [
    {
      title: 'Setup readiness',
      description:
        'Configuration milestones that must be completed before a tenant is considered production-ready. A fully checked list means the owner can sign in, pay, and start operating.',
      accentColor: '#E3008C',
      rows: [
        {
          label: 'Account published',
          checked: stats.publishedAt != null,
          hint: 'The owner has been emailed a working password and the temp credential admins were using has been rotated.'
        },
        {
          label: 'Root organisation has at least one approved context',
          checked: stats.rootOrganisationHasApprovedContext,
          hint:
            stats.rootOrganisationName != null
              ? `Approved OrganisationContextVersion exists for root org "${stats.rootOrganisationName}". Required before frameworks can be operationalised.`
              : 'Approved OrganisationContextVersion is required before frameworks can be operationalised.'
        },
        {
          label: 'Root organisation has billing address',
          checked: stats.rootOrganisationHasBillingAddress,
          hint: 'Used for invoicing and shown on exported reports. Set on the root organisation under Settings → Organisation.'
        },
        {
          label: 'Root organisation has identification number (IČO / equiv.)',
          checked: stats.rootOrganisationHasIdentificationNumber,
          hint: 'National business-registration number (IČO for SK/CZ, FN for AT, HRB for DE, etc.). Required for compliant invoicing.'
        },
        {
          label: 'Stripe customer connected',
          checked: stats.stripeCustomerConnected,
          hint: 'A StripeCustomer row exists — payment method is on file and recurring billing can run.'
        },
        {
          label: 'At least one framework selected',
          checked: stats.frameworkCount > 0,
          count: stats.frameworkCount,
          hint: 'FrameworkOnOrganisation rows. A tenant with zero frameworks has no compliance scope.'
        },
        {
          label: 'At least one framework configuration approved',
          checked: stats.approvedFrameworkConfigCount > 0,
          count: stats.approvedFrameworkConfigCount,
          hint: 'OrganisationFrameworkConfigVersion rows with isApproved=true. Approved configs are what assurance views and dashboards read from.'
        }
      ]
    },
    {
      title: 'Identity & access',
      description:
        'People who have a User record inside this tenant, broken down by status. Active is what counts for billing-relevant seat usage.',
      accentColor: '#0078D4',
      rows: [
        {
          label: 'Total users',
          count: stats.userCountTotal,
          hint: 'Every User row regardless of status.'
        },
        {
          label: 'Active users',
          count: stats.userCountActive,
          hint: 'Currently able to sign in and use the tenant. Status = active.'
        },
        {
          label: 'Invited users (not yet active)',
          count: stats.userCountInvited,
          hint: 'Invitation sent but the user has not completed first sign-in. Status = invited.'
        },
        {
          label: 'Archived users',
          count: stats.userCountArchived,
          hint: 'Soft-deleted users. Cannot sign in but historical attribution is preserved.'
        }
      ]
    },
    {
      title: 'Business context',
      description:
        'Inventory of all asset records grouped by type. Mirrors the categories used by the asset connection screens and the renumbering admin action.',
      accentColor: '#008272',
      rows: [
        {
          label: 'Business services',
          count: stats.businessServiceCount,
          hint: 'typeId = businessServices — outward-facing services the organisation delivers.'
        },
        {
          label: 'Business functions',
          count: stats.businessFunctionCount,
          hint: 'typeId = businessFunctions — organisational capabilities that support business services.'
        },
        {
          label: 'Enabling services (ICT)',
          count: stats.enablingServiceCount,
          hint: 'typeId = enablingServices — internal/supporting ICT services that enable the business.'
        },
        {
          label: 'Policies',
          count: stats.policyCount,
          hint: 'typeId = policy — governance documents linked to framework nodes.'
        },
        {
          label: 'Processes',
          count: stats.processCount,
          hint: 'typeId = process — documented operational procedures.'
        },
        {
          label: 'Goals',
          count: stats.goalCount,
          hint: 'typeId = goals — measurable objectives, often linked to audit findings.'
        },
        {
          label: 'Security projects',
          count: stats.securityProjectCount,
          hint: 'typeId = security-project — time-boxed remediation / improvement initiatives.'
        },
        {
          label: 'Other assets',
          count: stats.otherAssetCount,
          hint: 'Everything else with deleted=false: hardware, software, data, locations, etc. Excludes people, roles, groups and the structural types above.'
        }
      ]
    },
    {
      title: 'People & external parties',
      description:
        'Asset records of typeId = people, split by their relationship to the tenant. Drives stakeholder analysis and DORA / NIS2 interested-party reporting.',
      accentColor: '#00B7C3',
      rows: [
        {
          label: 'Customers',
          count: stats.customerCount,
          hint: 'subtypeId = people#customers.'
        },
        {
          label: 'Suppliers',
          count: stats.supplierCount,
          hint: 'subtypeId = people#suppliers OR organisation#vendor (third parties providing services).'
        },
        {
          label: 'Other interested parties',
          count: stats.interestedPartyCount,
          hint: 'typeId = people, excluding customers, suppliers and users. Regulators, partners, communities, etc.'
        }
      ]
    },
    {
      title: 'Risk management',
      description:
        'Risk register state. A "finalised" assessment is one where someone has clicked through to the end of the assessment flow and locked it in.',
      accentColor: '#D83B01',
      rows: [
        {
          label: 'Risks',
          count: stats.riskCount,
          hint: 'Risk rows with deleted=false in the risk register.'
        },
        {
          label: 'Risks with at least one finalised assessment',
          count: stats.riskAssessmentFinalisedCount,
          checked: stats.riskAssessmentFinalisedCount > 0,
          hint: 'Distinct risks that have any RiskAssessmentVersion with finalisedAt set — the first assessment flow was completed.'
        },
        {
          label: 'Risk assessment drafts',
          count: stats.riskAssessmentDraftCount,
          hint: 'RiskAssessmentVersion rows with status = draft. High numbers can indicate stalled assessment work.'
        }
      ]
    },
    {
      title: 'Compliance & activity',
      description:
        'Steady-state operational signals: monitoring (KPIs), incidents, regulatory tracking, and the most recent user activity.',
      accentColor: '#A4262C',
      rows: [
        {
          label: 'KPIs configured',
          count: stats.kpiCount,
          checked: stats.kpiCount > 0,
          hint: 'Kpi rows — measurement points for ongoing assurance.'
        },
        {
          label: 'Events logged (incidents)',
          count: stats.eventCount,
          hint: 'Event rows — security incidents and notable operational events recorded so far.'
        },
        {
          label: 'Compliance rules configured',
          count: stats.complianceRuleCount,
          hint: 'ComplianceRule rows — tenant-specific automated checks against framework nodes.'
        },
        {
          label: 'Legal register entries',
          count: stats.legalRegisterEntryCount,
          hint: 'LegalRegisterEntry rows — tracked laws, regulations and contractual obligations.'
        },
        {
          label: 'Last user login',
          hint:
            stats.lastUserLoginAt != null
              ? `Most recent User.lastLoginAt — ${new Date(stats.lastUserLoginAt).toLocaleString()}.`
              : 'No user has ever signed in to this tenant.',
          checked: stats.lastUserLoginAt != null
        }
      ]
    },
    {
      title: 'Education & training',
      description:
        'Awareness programme footprint — campaigns, video lessons, knowledge-check assessments, and the engagement that has actually happened so far.',
      accentColor: '#C239B3',
      rows: [
        {
          label: 'Campaigns',
          count: stats.campaignCount,
          checked: stats.campaignCount > 0,
          hint: 'Campaign rows — bundles of training content targeted at user groups.'
        },
        {
          label: 'Published campaigns',
          count: stats.campaignPublishedCount,
          hint: 'Campaigns with publicationStatus = published — visible to assigned users.'
        },
        {
          label: 'Videos',
          count: stats.videoCount,
          checked: stats.videoCount > 0,
          hint: 'Video rows — individual training video lessons.'
        },
        {
          label: 'Published videos',
          count: stats.videoPublishedCount,
          hint: 'Videos with publicationStatus = published — watchable by end users.'
        },
        {
          label: 'Video watch events',
          count: stats.videoWatchEventCount,
          hint: 'VideoWatchEvent rows — cumulative view events across all users and videos. A proxy for engagement.'
        },
        {
          label: 'Assessments (quizzes / questionnaires)',
          count: stats.assessmentCount,
          hint: 'Assessment rows — knowledge checks and questionnaires that can be sent to users or external parties.'
        },
        {
          label: 'Completed assessment submissions',
          count: stats.assessmentSubmissionCompletedCount,
          checked: stats.assessmentSubmissionCompletedCount > 0,
          hint: 'AssessmentSubmission rows with status = completed — users who finished a quiz.'
        },
        {
          label: 'Training access records',
          count: stats.trainingAccessCount,
          hint: 'TrainingAccess rows — explicit grants of training-content access to specific users.'
        }
      ]
    },
    {
      title: 'Tasks & workflow',
      description:
        'Internal ticket queue used to close audit findings, treat risks, and run remediation work. Overdue is the headline number to watch.',
      accentColor: '#515C6B',
      rows: [
        {
          label: 'Total tasks',
          count: stats.taskCount,
          hint: 'Every Task row regardless of status.'
        },
        {
          label: 'Open tasks (not started or in progress)',
          count: stats.taskOpenCount,
          hint: 'Status in {notStarted, inProgress}.'
        },
        {
          label: 'Completed tasks',
          count: stats.taskCompletedCount,
          hint: 'Status = completed. Closed tasks are tracked separately.'
        },
        {
          label: 'Overdue tasks',
          count: stats.taskOverdueCount,
          hint: 'Open tasks whose dueAt is in the past. Tasks without a due date are excluded.',
          checked: stats.taskOverdueCount === 0
        }
      ]
    },
    {
      title: 'Audits & findings',
      description:
        'Internal and external audits planned in the tenant, plus the findings those audits produced and how many remain open.',
      accentColor: '#8E562E',
      rows: [
        {
          label: 'Audit plans',
          count: stats.auditPlanCount,
          hint: 'AuditPlan rows — every internal or external audit scheduled in this tenant.'
        },
        {
          label: 'Audit plans in progress',
          count: stats.auditPlanInProgressCount,
          hint: 'Status = inProgress. Audit is actively running.'
        },
        {
          label: 'Audit plans completed',
          count: stats.auditPlanCompletedCount,
          hint: 'Status = completed. Findings should be tracked under their own row below.'
        },
        {
          label: 'Audit findings (total)',
          count: stats.auditFindingCount,
          hint: 'AuditFinding rows — every recorded finding regardless of severity or status.'
        },
        {
          label: 'Open findings (open or in progress)',
          count: stats.auditFindingOpenCount,
          hint: 'Status in {open, inProgress}. Closed / resolved / accepted findings are excluded.',
          checked: stats.auditFindingOpenCount === 0
        }
      ]
    },
    {
      title: 'Controls, measures & vulnerabilities',
      description:
        'The countermeasures the tenant is operating and the threats they are designed to mitigate. BIA records describe business-impact analysis per asset.',
      accentColor: '#498205',
      rows: [
        {
          label: 'Measures (controls)',
          count: stats.measureCount,
          hint: 'Measure rows — implemented or in-progress controls protecting assets, regardless of status.'
        },
        {
          label: 'Measures in steady-state monitoring',
          count: stats.measureMonitoredCount,
          hint: 'MeasureStatus = monitored — the control has been implemented and is now in ongoing monitoring.'
        },
        {
          label: 'BIAs started',
          count: stats.biaCount,
          hint: 'Distinct BIAs with at least one BiaVersion — i.e. someone has opened the BIA flow and put data in. Empty Bia shell rows (auto-created per asset) are excluded.'
        },
        {
          label: 'Catalogue vulnerabilities',
          count: stats.vulnerabilityCount,
          hint: 'Vulnerability rows — the global vulnerability catalogue this tenant draws from.'
        },
        {
          label: 'Organisation-applicable vulnerabilities',
          count: stats.organisationVulnerabilityCount,
          hint: 'OrganisationVulnerability rows — catalogue vulnerabilities marked as applicable to this organisation.'
        }
      ]
    },
    {
      title: 'Access validation',
      description:
        'Periodic checks that the right people still have the right access. Findings here block sign-off on access reviews.',
      accentColor: '#5C2D91',
      rows: [
        {
          label: 'Access validation checks configured',
          count: stats.accessValidationCheckCount,
          hint: 'AccessValidationCheck rows — recurring checks targeting users / roles / systems.'
        },
        {
          label: 'Open access-validation findings',
          count: stats.accessValidationFindingOpenCount,
          hint: 'AccessValidationFinding rows with status in {open, acknowledged}. Resolved and ignored findings are excluded.',
          checked: stats.accessValidationFindingOpenCount === 0
        }
      ]
    },
    {
      title: 'Integrations',
      description:
        'External systems wired into this tenant. SSO and connectors let the tenant pull identity and signal from outside the platform.',
      accentColor: '#107C10',
      rows: [
        {
          label: 'SSO provisioning configured',
          checked: stats.ssoConfigured,
          hint: 'An SsoProvisioning row exists on the root organisation — incoming SSO logins can be mapped to user records here.'
        },
        {
          label: 'Organisation connectors',
          count: stats.connectorCount,
          hint: 'OrganisationConnector rows — directory sync, ticketing, asset discovery and similar integrations.'
        }
      ]
    }
  ];
}

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 0) return new Date(iso).toLocaleDateString();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

const accountStatisticsQuery = gql<AccountStatisticsGQL>`
  query AccountStatistics($id: ID!) {
    accountStatistics(id: $id) {
      accountId
      alias
      createdAt
      publishedAt
      userCountTotal
      userCountActive
      userCountInvited
      userCountArchived
      lastUserLoginAt
      organisationCount
      rootOrganisationId
      rootOrganisationName
      rootOrganisationHasApprovedContext
      rootOrganisationHasBillingAddress
      rootOrganisationHasIdentificationNumber
      frameworkCount
      approvedFrameworkConfigCount
      stripeCustomerConnected
      businessServiceCount
      businessFunctionCount
      enablingServiceCount
      policyCount
      processCount
      goalCount
      securityProjectCount
      otherAssetCount
      customerCount
      supplierCount
      interestedPartyCount
      riskCount
      riskAssessmentFinalisedCount
      riskAssessmentDraftCount
      kpiCount
      eventCount
      campaignCount
      campaignPublishedCount
      videoCount
      videoPublishedCount
      videoWatchEventCount
      assessmentCount
      assessmentSubmissionCompletedCount
      trainingAccessCount
      taskCount
      taskOpenCount
      taskCompletedCount
      taskOverdueCount
      auditPlanCount
      auditPlanCompletedCount
      auditPlanInProgressCount
      auditFindingCount
      auditFindingOpenCount
      measureCount
      measureMonitoredCount
      biaCount
      vulnerabilityCount
      organisationVulnerabilityCount
      accessValidationCheckCount
      accessValidationFindingOpenCount
      complianceRuleCount
      ssoConfigured
      connectorCount
      legalRegisterEntryCount
    }
  }
`;
